'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  GraduationCap,
  Brain,
  Users,
  Award,
  BarChart3,
  PlayCircle,
  MessageSquare,
  CheckCircle2,
  Target,
  BookOpen,
  Trophy,
  Clock
} from 'lucide-react'

import { FadeUp } from '@/components/ui/fade-up'
const features = [
  {
    icon: BookOpen,
    title: 'コース管理',
    description: '体系的なカリキュラム設計。動画、テキスト、演習を組み合わせた効果的な学習体験。'
  },
  {
    icon: Brain,
    title: '小テスト・演習',
    description: '理解度を確認する多様な問題形式。AIによる自動採点と個別フィードバック。'
  },
  {
    icon: Award,
    title: '修了証・オープンバッジ',
    description: '国際標準のオープンバッジ発行。学習成果を可視化し、キャリアに活用。'
  },
  {
    icon: BarChart3,
    title: '進捗管理',
    description: '個人・組織単位の学習進捗を可視化。つまずきポイントの早期発見と対策。'
  },
  {
    icon: MessageSquare,
    title: 'ディスカッション',
    description: '受講者同士の学び合い。メンターによるサポートで実践的な理解を深める。'
  },
  {
    icon: PlayCircle,
    title: 'ライブ配信・録画',
    description: 'リアルタイム授業と録画視聴の併用。いつでもどこでも学習可能。'
  }
]

const courses = [
  {
    category: 'IT基礎',
    title: 'DXリテラシー基礎講座',
    description: '非エンジニアでもわかるDXの基本概念と活用方法',
    duration: '全10回（各60分）',
    target: '自治体職員、地域事業者',
    topics: [
      'DXとは何か',
      'クラウドサービスの基礎',
      'データ活用入門',
      'セキュリティ基礎'
    ]
  },
  {
    category: 'AI活用',
    title: '生成AI実践講座',
    description: '業務で使える生成AIの活用方法を実践的に学習',
    duration: '全8回（各90分）',
    target: '業務担当者、管理職',
    topics: [
      'プロンプトエンジニアリング',
      '業務自動化の設計',
      'AIガバナンス',
      '倫理とリスク管理'
    ]
  },
  {
    category: '市民参加',
    title: 'デジタル市民講座',
    description: 'デジタル社会に参加するための基礎スキル習得',
    duration: '全6回（各45分）',
    target: '一般市民、シニア層',
    topics: [
      'スマートフォン活用',
      'オンライン行政手続き',
      'デジタル決済',
      '情報リテラシー'
    ]
  }
]

const results = [
  {
    metric: '累計受講者数',
    value: '15,000名+',
    description: '全国の自治体・企業で活用'
  },
  {
    metric: '修了率',
    value: '85%',
    description: '業界平均の2倍以上'
  },
  {
    metric: '実践活用率',
    value: '73%',
    description: '学んだ内容を実務で活用'
  },
  {
    metric: '満足度',
    value: '4.8/5.0',
    description: '受講者アンケート結果'
  }
]

export function LmsContent() {
  return (
    <>
      {/* ヒーローセクション */}
      <section className="relative bg-gradient-to-br from-green-600 via-green-700 to-emerald-800 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/images/grid.svg')] opacity-20"></div>
        <div
          className="animate-fade-up container relative z-10"
        >
          <div className="max-w-4xl mx-auto">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">IT/AI・市民参加の学び</Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              LMS
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              学び → 実践へ確実に繋ぐ仕組み
            </p>
            <p className="text-lg mb-8 opacity-80">
              IT/AIリテラシー、実装人材の育成、参加者教育。<br />
              コース、小テスト、修了証（オープンバッジ）、進捗管理で<br />
              地域のデジタル人材を体系的に育成します。
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" variant="secondary">
                デモ環境を体験
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                コース一覧を見る
              </Button>
            </div>
          </div>
        </div>
        <div className="absolute -bottom-1 left-0 right-0">
          <svg viewBox="0 0 1440 100" className="w-full h-24 fill-background">
            <path d="M0,50 C360,100 1080,0 1440,50 L1440,100 L0,100 Z" />
          </svg>
        </div>
      </section>

      {/* 実績 */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">確かな実績</h2>
            <p className="text-lg text-muted-foreground">
              地域のデジタル人材育成を支える信頼の実績
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-4 gap-6">
            {results.map((result, index) => (
              <FadeUp delay={index * 0.1}
                key={index}
              >
                <Card className="text-center">
                  <CardContent className="pt-6">
                    <div className="text-4xl font-bold text-primary mb-2">
                      {result.value}
                    </div>
                    <div className="font-semibold mb-1">{result.metric}</div>
                    <p className="text-sm text-muted-foreground">{result.description}</p>
                  </CardContent>
                </Card>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* 主要機能 */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">主要機能</h2>
            <p className="text-lg text-muted-foreground">
              効果的な学習を実現する充実した機能群
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon
              return (
                <FadeUp delay={index * 0.05}
                  key={index}
                >
                  <Card className="h-full">
                    <CardHeader>
                      <Icon className="h-8 w-8 text-primary mb-2" />
                      <CardTitle className="text-lg">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </CardContent>
                  </Card>
                </FadeUp>
              )
            })}
          </div>
        </div>
      </section>

      {/* コース例 */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">提供コース例</h2>
            <p className="text-lg text-muted-foreground">
              地域のニーズに応じた多様なコースを提供
            </p>
          </FadeUp>

          <Tabs defaultValue="it" className="max-w-5xl mx-auto">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="it">IT基礎</TabsTrigger>
              <TabsTrigger value="ai">AI活用</TabsTrigger>
              <TabsTrigger value="citizen">市民参加</TabsTrigger>
            </TabsList>

            {courses.map((course, index) => (
              <TabsContent 
                key={index} 
                value={index === 0 ? 'it' : index === 1 ? 'ai' : 'citizen'}
                className="mt-8"
              >
                <Card>
                  <CardHeader>
                    <Badge className="w-fit mb-2">{course.category}</Badge>
                    <CardTitle className="text-2xl">{course.title}</CardTitle>
                    <CardDescription>{course.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold mb-3">コース概要</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span>{course.duration}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <span>対象: {course.target}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-3">主な学習内容</h4>
                        <ul className="space-y-1">
                          {course.topics.map((topic, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm">
                              <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                              <span>{topic}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="flex gap-4 mt-6">
                      <Button>
                        シラバスを見る
                      </Button>
                      <Button variant="outline">
                        サンプル講義を視聴
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* 学習フロー */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">学習から実践までの流れ</h2>
            <p className="text-lg text-muted-foreground">
              体系的なカリキュラムで確実にスキルを身につける
            </p>
          </FadeUp>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {[
                {
                  step: '01',
                  title: '事前診断',
                  description: '現在のスキルレベルを診断し、最適な学習プランを提案',
                  icon: Target
                },
                {
                  step: '02',
                  title: '基礎学習',
                  description: '動画講義とテキストで基本概念を理解',
                  icon: BookOpen
                },
                {
                  step: '03',
                  title: '実践演習',
                  description: '実際の業務を想定した演習問題で理解を深める',
                  icon: Brain
                },
                {
                  step: '04',
                  title: '評価・認定',
                  description: '修了テストに合格すると、オープンバッジを発行',
                  icon: Trophy
                },
                {
                  step: '05',
                  title: 'フォローアップ',
                  description: '実務での活用をサポート。追加学習コンテンツも提供',
                  icon: Users
                }
              ].map((phase, index) => {
                const Icon = phase.icon
                return (
                  <FadeUp delay={index * 0.1}
                    key={index}
                    className="flex gap-6"
                  >
                    <div className="shrink-0">
                      <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center font-bold">
                        {phase.step}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2 flex items-center gap-2">
                        {phase.title}
                        <Icon className="h-5 w-5 text-primary" />
                      </h3>
                      <p className="text-muted-foreground">{phase.description}</p>
                    </div>
                  </FadeUp>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 導入事例 */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">導入事例</h2>
            <p className="text-lg text-muted-foreground">
              様々な組織で人材育成の成果を実現
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                organization: '広島県庁',
                program: 'DX人材育成プログラム',
                participants: '職員500名',
                result: 'デジタル化推進リーダー50名を育成',
                quote: '実践的な内容で、すぐに業務に活かせました'
              },
              {
                organization: '福山商工会議所',
                program: '中小企業向けAI活用講座',
                participants: '経営者・管理職120名',
                result: '参加企業の70%がAI導入を実現',
                quote: 'AIへの不安が解消され、積極的に活用できるように'
              },
              {
                organization: '尾道市',
                program: 'シニア向けデジタル講座',
                participants: '市民300名',
                result: 'オンライン行政手続き利用率が50%向上',
                quote: '丁寧な説明で、スマホが怖くなくなりました'
              },
              {
                organization: '呉高専',
                program: '地域DX実践講座',
                participants: '学生80名',
                result: '地域企業との共同プロジェクト10件実施',
                quote: '学んだことを実際のプロジェクトで活かせる'
              }
            ].map((caseStudy, index) => (
              <FadeUp delay={index * 0.1}
                key={index}
              >
                <Card>
                  <CardHeader>
                    <CardTitle>{caseStudy.organization}</CardTitle>
                    <CardDescription>{caseStudy.program}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-4 text-sm">
                      <Badge variant="secondary">{caseStudy.participants}</Badge>
                    </div>
                    <div className="pt-4 border-t">
                      <p className="font-semibold text-primary mb-2">{caseStudy.result}</p>
                      <p className="text-sm text-muted-foreground italic">&ldquo;{caseStudy.quote}&rdquo;</p>
                    </div>
                  </CardContent>
                </Card>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* 料金プラン */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">料金プラン</h2>
            <p className="text-lg text-muted-foreground">
              組織の規模と目的に応じた柔軟なプラン
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                name: 'ライト',
                price: '月額 10万円〜',
                description: '小規模組織・部門単位',
                features: [
                  'ユーザー数: 〜50名',
                  '基本コース: 10種類',
                  '月次レポート',
                  'メールサポート',
                  'オープンバッジ発行'
                ]
              },
              {
                name: 'スタンダード',
                price: '月額 30万円〜',
                description: '中規模組織・自治体',
                features: [
                  'ユーザー数: 〜500名',
                  '全コースアクセス',
                  'カスタムコース作成',
                  '詳細分析レポート',
                  '優先サポート',
                  'API連携'
                ],
                popular: true
              },
              {
                name: 'エンタープライズ',
                price: 'お問い合わせ',
                description: '大規模組織・広域連携',
                features: [
                  'ユーザー数: 無制限',
                  'フルカスタマイズ',
                  '専用インスタンス',
                  'SSOシングルサインオン',
                  '専任サポートチーム',
                  'オンサイト研修'
                ]
              }
            ].map((plan, index) => (
              <FadeUp delay={index * 0.1}
                key={index}
              >
                <Card className={`h-full ${plan.popular ? 'border-primary shadow-lg' : ''}`}>
                  {plan.popular && (
                    <div className="bg-primary text-white text-center py-2 text-sm font-semibold">
                      人気プラン
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle>{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                    <div className="text-3xl font-bold mt-4">{plan.price}</div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full mt-6" variant={plan.popular ? 'default' : 'outline'}>
                      {plan.name === 'エンタープライズ' ? 'お問い合わせ' : '無料トライアル'}
                    </Button>
                  </CardContent>
                </Card>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-emerald-700 text-white">
        <div className="container">
          <FadeUp
            className="max-w-3xl mx-auto text-center"
          >
            <GraduationCap className="h-16 w-16 mx-auto mb-6 opacity-80" />
            <h2 className="text-3xl font-bold mb-4">
              地域の未来を担う人材を、一緒に育てませんか？
            </h2>
            <p className="text-xl mb-8 opacity-90">
              まずはデモ環境で、効果的な学習体験をご確認ください
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary">
                デモ環境を体験
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                導入相談を予約
              </Button>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  )
}